import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import AppContent from './AppContent';
import axios from 'axios';
import Loader from './components/Loader';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Mantenimiento from './components/Mantenimiento';
import BASE_URL from './config/config';
import ScrollToTop from './components/ScrollToTop';
import { useVigilanciaSesion, tokenCaducado, cerrarSesion } from './auth/sesion';

// Prefijos de ruta que pertenecen a la app autenticada: usan la sidebar
// lateral fija en vez del Header superior. Todo lo demás es público
// (landing, login, registro...) y conserva el Header de siempre.
const PREFIJOS_APP = ['/dashboard', '/nuevo-ticket', '/ticket-creado', '/ticket/'];
const esRutaApp = (pathname) =>
  PREFIJOS_APP.some((prefijo) => pathname === prefijo || pathname.startsWith(`${prefijo}/`) || pathname.startsWith(prefijo));

const AppShell = ({ authenticated, userRole, tokenInfo }) => {
  const location = useLocation();
  const rutasSinLayout = ['/iniciar-sesion', '/recuperar-contrasena', '/editar-contrasena', '/registrarse'];
  const rutaSinLayout = rutasSinLayout.some((ruta) => location.pathname === ruta || location.pathname === `${ruta}/`);

  const token = localStorage.getItem('AuthToken');
  let estadoCuenta = null;
  if (token) {
    try {
      estadoCuenta = jwtDecode(token).estado;
    } catch {
      estadoCuenta = null;
    }
  }
  const verificacionEnProceso = authenticated && estadoCuenta === 'verificar';
  const sinLayout = rutaSinLayout || verificacionEnProceso;
  const usarSidebar = authenticated && !sinLayout && esRutaApp(location.pathname);

  return (
    <>
      <ScrollToTop />
      {usarSidebar ? (
        <Sidebar />
      ) : (
        !sinLayout && <Header authenticated={authenticated} />
      )}
      <div className={usarSidebar ? 'app-shell-sidebar' : undefined}>
        <AppContent
          authenticated={authenticated}
          userRole={userRole}
          tokenInfo={tokenInfo}
        />
      </div>
      {!sinLayout && !usarSidebar && <Footer />}
    </>
  );
};

const Routering = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enMantenimiento, setEnMantenimiento] = useState(false);
  const [verificandoMantenimiento, setVerificandoMantenimiento] = useState(true);

  useEffect(() => {
    const verificarMantenimiento = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/ajustes/en_mantenimiento`);
        if (response.data.success && response.data.ajuste.valor === 'si') {
          setEnMantenimiento(true);
        }
      } catch (error) {
        console.error('Error al verificar mantenimiento:', error);
      } finally {
        setVerificandoMantenimiento(false);
      }
    };

    verificarMantenimiento();
  }, []);

  useEffect(() => {
    const verificarToken = async () => {
      try {
        const token = localStorage.getItem('AuthToken');
        if (!token) {
          throw new Error('No se encontró token');
        }

        // Token caducado al abrir la app: fuera al login con el aviso, sin
        // llegar a pintar nada de la zona privada.
        if (tokenCaducado(token)) {
          cerrarSesion({ caducada: true });
          throw new Error('Sesión caducada');
        }

        const response = await axios.get(`${BASE_URL}/autenticacion`, {
          headers: {
            Authorization: `${token}`,
          },
        });

        setAuthenticated(true);
        setUserRole(response.data.role);
        setTokenInfo(response.data);
      } catch (error) {
        setAuthenticated(false);
        setUserRole(null);
        setTokenInfo(null);
        localStorage.removeItem('AuthToken');
      } finally {
        setLoading(false);
      }
    };

    verificarToken();
  }, []);

  // Cierre automático en cuanto el token expira, sin esperar a la siguiente petición.
  useVigilanciaSesion(authenticated);

  return (
    <Router>
      {loading || verificandoMantenimiento ? (
        <Loader />
      ) : enMantenimiento && userRole !== 'admin' && userRole !== 'tecnico' ? (
        <Mantenimiento />
      ) : (
        <AppShell authenticated={authenticated} userRole={userRole} tokenInfo={tokenInfo} />
      )}
    </Router>
  );
};

export default Routering;
